//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect } from 'react';

interface AnimatedAIIconProps {
  src: string;
  alt: string;
  isProcessing?: boolean;
  style?: React.CSSProperties;
}

const AnimatedAIIcon: React.FC<AnimatedAIIconProps> = ({
  src,
  alt,
  isProcessing = false,
  style = {},
}) => {
  // Inject keyframe animations into the document
  useEffect(() => {
    const styleId = 'ai-icon-keyframes';

    // Check if styles already exist
    if (document.getElementById(styleId)) {
      return;
    }

    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;

    // Convert aiGlobalStyles keyframes to CSS
    const keyframesCSS = `
      @keyframes aiIconBreathe {
        0% { opacity: 0.3; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.1); }
        100% { opacity: 0.3; transform: scale(1); }
      }
      
      @keyframes aiIconScale {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      @keyframes aiIconRotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes aiIconFade {
        0% { opacity: 0; }
        50% { opacity: 0.3; }
        100% { opacity: 0; }
      }
    `;

    styleSheet.textContent = keyframesCSS;
    document.head.appendChild(styleSheet);

    // Cleanup on unmount
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // NOTE: Inline CSS is used here for animation styles that cannot be expressed
  // as Tailwind utilities (dynamic keyframe-based animations driven by isProcessing state).
  const safeIconStyles = {
    container: {
      position: 'relative' as const,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    glow: {
      position: 'absolute' as const,
      inset: 0,
      borderRadius: '50%',
      backgroundColor: 'rgba(25, 118, 210, 0.4)',
      filter: 'blur(8px)',
      animation: 'aiIconBreathe 2s ease-in-out infinite',
      opacity: 0,
    },
    ring: {
      position: 'absolute' as const,
      inset: '-4px',
      borderRadius: '50%',
      border: '2px solid rgba(25, 118, 210, 0.3)',
      animation: 'aiIconBreathe 2s ease-in-out infinite 0.5s',
      opacity: 0,
    },
    image: {
      position: 'relative' as const,
      animation: 'aiIconScale 2s ease-in-out infinite',
      filter: 'drop-shadow(0 0 8px rgba(25, 118, 210, 0.5))',
    },
    shimmer: {
      position: 'absolute' as const,
      inset: 0,
      borderRadius: '50%',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, transparent 50%, rgba(255,255,255,0.6) 100%)',
      animation:
        'aiIconRotate 3s linear infinite, aiIconFade 2s ease-in-out infinite',
      opacity: 0,
      pointerEvents: 'none' as const,
    },
  };

  const containerStyle: React.CSSProperties = {
    ...safeIconStyles.container,
    ...style,
  };

  // Strip margin properties from image style so the image centers properly
  // within the container; margins should only apply to the outer container.
  const {
    margin,
    marginRight,
    marginLeft,
    marginTop,
    marginBottom,
    ...imageStyle
  } = style as React.CSSProperties & Record<string, unknown>;

  if (!isProcessing) {
    return <img src={src} alt={alt} style={style} />;
  }

  return (
    <div
      id="id-ai-icon-container"
      data-testid="ai-icon-container"
      style={containerStyle}
    >
      <div
        id="id-ai-icon-glow"
        data-testid="ai-icon-glow"
        style={safeIconStyles.glow}
      />
      <div
        id="id-ai-icon-ring"
        data-testid="ai-icon-ring"
        style={safeIconStyles.ring}
      />
      <img
        src={src}
        alt={alt}
        style={{ ...imageStyle, ...safeIconStyles.image }}
      />
      <div
        id="id-ai-icon-shimmer"
        data-testid="ai-icon-shimmer"
        style={safeIconStyles.shimmer}
      />
    </div>
  );
};

export default AnimatedAIIcon;
